/**
 * Conversation-channel cards — pure extraction logic (no React), shared by the
 * Agent SDK doors that display a HEADLESS conversation agent's transcript (the
 * Quote Advisor behind the expired-quote alert; "View Merlin's conversation").
 *
 * A headless agent talks to a human over email / SMS through the conversation
 * channels, and its structured output is a pair of CAPTURE tool calls
 * (services/agent_console/tools/conversation_reply.py, tools/expired_quote.py):
 *
 *   send_conversation_message   → the message the person actually received
 *   record_expired_quote_decision → the decision applied to the alert
 *
 * Rendered as chips those frames hide the conversation behind a "view trace"
 * toggle, so this module lifts the ACCEPTED frames out of a turn's block trace
 * into card models (the card replaces the chip) — the transcript then reads as
 * the exchange it was. A rejected frame (the tool handler said no — too long,
 * a code fence…) keeps its chip so the trace still shows what happened.
 */

import type { AssistantBlock } from '../../services/agentConsole';

export interface SentMessageData {
  key: string;
  /** The Markdown form (email / default). */
  markdown: string | null;
  /** The text-message form, when the agent wrote one. */
  sms: string | null;
}

export type DecisionSource = 'salesperson' | 'standing_rule';

export interface ConversationDecisionData {
  key: string;
  decision: 'approve' | 'deny';
  source: DecisionSource;
  basis: string;
}

export function isSendConversationMessageTool(name: string): boolean {
  return (
    name === 'send_conversation_message' || name.endsWith('__send_conversation_message')
  );
}

export function isRecordExpiredQuoteDecisionTool(name: string): boolean {
  return (
    name === 'record_expired_quote_decision' ||
    name.endsWith('__record_expired_quote_decision')
  );
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** Card model for an ACCEPTED send frame; null when nothing deliverable. */
export function sentMessageFromInput(
  key: string,
  input: Record<string, unknown>,
): SentMessageData | null {
  const markdown = nonEmptyString(input.message_markdown);
  const sms = nonEmptyString(input.message_sms);
  if (!markdown && !sms) return null;
  return { key, markdown, sms };
}

/** Card model for an ACCEPTED decision frame; null when malformed. */
export function decisionFromInput(
  key: string,
  input: Record<string, unknown>,
): ConversationDecisionData | null {
  const decision = input.decision;
  const source = input.source;
  const basis = nonEmptyString(input.basis);
  if (decision !== 'approve' && decision !== 'deny') return null;
  if (source !== 'salesperson' && source !== 'standing_rule') return null;
  if (!basis) return null;
  return { key, decision, source, basis };
}

export interface LiftedConversationCards {
  blocks: AssistantBlock[];
  sent: SentMessageData[];
  decisions: ConversationDecisionData[];
}

/**
 * Pull every ACCEPTED capture frame out of the blocks. A pending (mid-stream)
 * or errored frame keeps its chip. A turn normally sends once and decides at
 * most once, but the lift accumulates so a corrected re-send still shows both.
 */
export function liftConversationCards(blocks: AssistantBlock[]): LiftedConversationCards {
  const sent: SentMessageData[] = [];
  const decisions: ConversationDecisionData[] = [];
  const kept: AssistantBlock[] = [];
  for (const block of blocks) {
    if (block.kind === 'tool' && block.tool.status === 'ok') {
      if (isSendConversationMessageTool(block.tool.rawName)) {
        const card = sentMessageFromInput(block.tool.key, block.tool.input);
        if (card) {
          sent.push(card);
          continue; // card replaces the chip
        }
      } else if (isRecordExpiredQuoteDecisionTool(block.tool.rawName)) {
        const card = decisionFromInput(block.tool.key, block.tool.input);
        if (card) {
          decisions.push(card);
          continue;
        }
      }
    }
    kept.push(block);
  }
  return { blocks: kept, sent, decisions };
}

export function decisionHeadline(d: ConversationDecisionData): string {
  return d.decision === 'approve'
    ? 'Decision: honor the expired quote price'
    : 'Decision: adjust the price before invoicing';
}

export function decisionSourceLabel(source: DecisionSource): string {
  return source === 'standing_rule'
    ? "from the salesperson's standing rule"
    : "from the salesperson's own words";
}
